import { FC, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Field, Form, FormSpy } from 'react-final-form';
import { enqueueSnackbar } from 'notistack';

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LoadingButton,
    SelectFinalField,
    TextFinalField,
} from '@thirdspacelearning/library';
import { required } from '@tsl-frontend/tsl-utilities';

import { useUpdateProgramme } from '../../hooks';
import { ProgrammeType } from '../../types';
import {
    gradeOptions,
    keyStageOptions,
    stateStandardOptions,
} from '../../utils';

import { createProgramme } from '@api';
import { Country } from '@types';
import { countryOptions } from '@utils';
import { CheckboxFinalField, Condition } from '@shared';

const sx = {
    dialog: {
        '& .MuiDialog-paper': {
            p: 3,
            gap: 2,
        },
    },
};

type Props = {
    open: boolean;
    onClose: (show: boolean) => void;
    programme?: ProgrammeType;
};

/**
 * Programme Form used to create or edit a programme.
 */
const ProgrammeForm: FC<Props> = ({ open, onClose, programme }) => {
    const navigate = useNavigate();

    const [isPristine, setPristine] = useState(true);

    const { mutate: createNewProgramme, isPending: isCreating } = useMutation<
        ProgrammeType,
        Error,
        Partial<ProgrammeType>
    >({
        mutationFn: (params: Partial<ProgrammeType>) => createProgramme(params),
        onSuccess: (data) => {
            navigate(`/programmes/${data.id}`);
        },
        onError: (error: Error) => {
            enqueueSnackbar(error.message, {
                title: 'There was an error creating the programme',
                key: 'create-programme-error',
                testId: 'create-programme-error',
                variant: 'curriculumSnackbar',
                severity: 'error',
            });
        },
    });

    const { updateProgramme, isUpdating } = useUpdateProgramme(
        programme?.id,
        'Programme edited successfully!',
        undefined,
        () => onClose(false),
    );

    const onSubmit = (values: any) => {
        const defaultData = {
            country: values.country,
            title: values.title,
            description: values.description,
            year_id: Number(values.year_id),
            ai: values.ai,
        };

        let countryData = {};

        if (values.country === Country.GB) {
            const { key_stage } = values;

            countryData = {
                ...defaultData,
                key_stage: Number(key_stage),
            };
        } else if (values.country === Country.US) {
            const { state_standard } = values;

            countryData = {
                ...defaultData,
                state_standard,
            };
        } else {
            throw new Error('Invalid country');
        }

        if (programme) {
            updateProgramme(countryData);
        } else {
            createNewProgramme(countryData);
        }
    };

    const handleClose = (event: any, reason: string) => {
        if (reason && reason === 'backdropClick') {
            return;
        }

        onClose(false);
    };

    const isPending = isCreating || isUpdating;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            sx={sx.dialog}
            data-testid="programme-form"
        >
            <DialogTitle align="center" fontWeight="bold" sx={{ p: 0 }}>
                {programme ? 'Edit programme' : 'Create new programme'}
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                <Form
                    onSubmit={onSubmit}
                    initialValues={{
                        country: Country.GB,
                        ai: true,
                        ...programme,
                    }}
                    render={({ handleSubmit, values, form }) => (
                        <form
                            onSubmit={handleSubmit}
                            id="createNewProgramme"
                            noValidate
                        >
                            <Box
                                display="flex"
                                flexDirection="column"
                                width="450px"
                            >
                                <Field
                                    name="country"
                                    component={SelectFinalField}
                                    label="Region *"
                                    options={countryOptions}
                                    variant="outlined"
                                    validate={required}
                                    required
                                    fullWidth
                                    onChangeCallback={() => {
                                        form.change('year_id', undefined);
                                        form.resetFieldState('year_id');
                                    }}
                                />
                                <Field
                                    name="title"
                                    component={TextFinalField}
                                    label="Programme name"
                                    placeholder="Input text"
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ maxLength: 45 }}
                                    validate={required}
                                    required
                                    fullWidth
                                    sx={{ mb: 1 }}
                                    data-testid="title-input"
                                />
                                <Field
                                    name="description"
                                    component={TextFinalField}
                                    label="Programme description"
                                    placeholder="Input text"
                                    InputLabelProps={{ shrink: true }}
                                    validate={required}
                                    inputProps={{ maxLength: 180 }}
                                    maxRows={3}
                                    multiline
                                    required
                                    fullWidth
                                    sx={{ my: 2 }}
                                    data-testid="description-input"
                                />
                                <Condition when="country" is={Country.GB}>
                                    <Field
                                        name="key_stage"
                                        component={SelectFinalField}
                                        label="Key Stage *"
                                        options={keyStageOptions}
                                        variant="outlined"
                                        validate={required}
                                        required
                                        fullWidth
                                        parse={(value) =>
                                            value != null ? String(value) : ''
                                        }
                                        format={(value) =>
                                            value != null ? String(value) : ''
                                        }
                                        onChangeCallback={() => {
                                            form.change('year_id', undefined);
                                            form.resetFieldState('year_id');
                                        }}
                                    />
                                    <Field
                                        name="year_id"
                                        component={SelectFinalField}
                                        label="Core year group *"
                                        options={
                                            values.key_stage
                                                ? keyStageOptions.find(
                                                      (option) =>
                                                          option.id ===
                                                          `${values.key_stage}`,
                                                  )?.yearGroups
                                                : []
                                        }
                                        variant="outlined"
                                        disabled={!values.key_stage}
                                        validate={required}
                                        required
                                        fullWidth
                                        parse={(value) =>
                                            value != null ? String(value) : ''
                                        }
                                        format={(value) =>
                                            value != null ? String(value) : ''
                                        }
                                    />
                                </Condition>
                                <Condition when="country" is={Country.US}>
                                    <Field
                                        name="state_standard"
                                        component={SelectFinalField}
                                        label="State standard *"
                                        options={stateStandardOptions}
                                        variant="outlined"
                                        validate={required}
                                        required
                                        fullWidth
                                    />
                                    <Field
                                        name="year_id"
                                        component={SelectFinalField}
                                        label="Grade*"
                                        options={gradeOptions}
                                        variant="outlined"
                                        validate={required}
                                        required
                                        fullWidth
                                        parse={(value) =>
                                            value != null ? String(value) : ''
                                        }
                                        format={(value) =>
                                            value != null ? String(value) : ''
                                        }
                                    />
                                </Condition>
                                <Field
                                    name="ai"
                                    type="checkbox"
                                    label="AI Programme"
                                    component={CheckboxFinalField}
                                />
                            </Box>
                            <FormSpy
                                subscription={{
                                    pristine: true,
                                }}
                                onChange={({ pristine }) => {
                                    if (!pristine) {
                                        setPristine(false);
                                    }
                                }}
                            />
                        </form>
                    )}
                />
            </DialogContent>
            <DialogActions sx={{ display: 'flex' }}>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => onClose(false)}
                    fullWidth
                >
                    Close
                </Button>
                <LoadingButton
                    variant="contained"
                    color="primary"
                    type="submit"
                    form="createNewProgramme"
                    fullWidth
                    loading={isPending}
                    disabled={isPristine}
                    data-testid="programme-submit-button"
                >
                    {programme ? 'Confirm' : 'Create programme'}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default ProgrammeForm;
